// ---------------------------------------------------------------------------
// TestSet Service — Types and interface for the Teams Eval Data Hub API.
//
// API spec: POST /testsets/search, POST /testsets, GET /testsets/{id}, etc.
// TestSets are derived subsets from Datasets — they reference data entries
// from a parent dataset.
// ---------------------------------------------------------------------------

import type { Dataset, DataEntry } from './dataHubService.interface.js';
import type { UsageContextOverrides } from './usageContext.interface.js';

// ── TestSet types ────────────────────────────────────────────────────────────

/**
 * A test set record. Extends Dataset with parent-dataset linkage.
 * Shares the same list-row shape so DatasetList can render both.
 */
export interface TestSet extends Dataset {
  testSetId: string;
  dataSetId: string;
  ownerUserObjectId?: string;
  tenantId?: string;
}

export enum AccessCategory {
  Public = 0,
  Restricted = 1,
}

export enum SensitivityLabel {
  General = 0,
  Confidential = 1,
  HighlyConfidential = 2,
}

// ── Request / response shapes ───────────────────────────────────────────────

export interface SearchTestSetsParams {
  query?: string;
  ownerUserObjectId?: string;
  dataSetIds?: string[];
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

export interface TestSetSearchResult {
  items: TestSet[];
  totalCount: number | null;
  continuationToken: string | null;
}

export interface CreateTestSetRequest {
  dataSetId: string;
  name: string;
  sensitivityLabel: SensitivityLabel;
  accessCategory: AccessCategory;
  description?: string;
}

export interface UpdateTestSetRequest {
  name?: string;
  description?: string;
}

export interface TestSetVersionListParams {
  testSetId: string;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

export interface TestSetVersionListResult {
  items: TestSet[];
  totalCount: number | null;
  continuationToken: string | null;
}

export interface TestSetDataListParams {
  testSetId: string;
  version: number;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

export interface TestSetDataListResult {
  items: DataEntry[];
  totalCount: number | null;
  continuationToken: string | null;
}

// ── Service interface ───────────────────────────────────────────────────────

export interface ITestSetService {
  searchTestSets(params: SearchTestSetsParams): Promise<TestSetSearchResult>;
  createTestSet(
    request: CreateTestSetRequest,
    usage?: UsageContextOverrides,
  ): Promise<TestSet>;
  getTestSet(testSetId: string, version?: number): Promise<TestSet>;
  updateTestSet(
    testSetId: string,
    request: UpdateTestSetRequest,
  ): Promise<TestSet>;
  deleteTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;
  publishTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<TestSet>;
  createDraftTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<TestSet>;
  deleteTestSetDraft(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;
  listTestSetVersions(
    params: TestSetVersionListParams,
  ): Promise<TestSetVersionListResult>;
  addTestSetData(
    testSetId: string,
    dataIds: string[],
    usage?: UsageContextOverrides,
  ): Promise<void>;
  listTestSetData(
    params: TestSetDataListParams,
  ): Promise<TestSetDataListResult>;
  deleteTestSetData(
    testSetId: string,
    dataIds: string[],
    usage?: UsageContextOverrides,
  ): Promise<void>;
}
